/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import {
  ArrowRight, ArrowsClockwise, Trophy, GameController, House,
  SpeakerHigh, SpeakerSlash, ShieldCheck, Warning, Sparkle,
  Syringe, Flask, Drop, Bandaids, Pill, FirstAid,
  TestTube, Hand, Heartbeat, Trash, ShareNetwork,
} from "@phosphor-icons/react";

/* ============================================================
 * BIOMEDICAL SEGREGATION GAME
 * Pure client-side. Drag waste cards into the correct color-coded bin.
 * - 3 levels (Beginner / Intermediate / Advanced)
 * - Score, streak, compliance facts
 * - Web-Audio synthesized SFX with mute toggle
 * ============================================================ */

const BINS = [
  {
    id: "yellow", label: "Yellow Bin",
    sub: "Anatomical · Soiled · Diapers · Sanitary",
    color: "#f59e0b", soft: "#fef3c7", dark: "#92400e",
    fact: "Yellow (BMW Rules 2016): human anatomical, soiled & expired waste — sent for incineration.",
  },
  {
    id: "red", label: "Red Bin",
    sub: "Tubes · IV sets · Plastic disposables · Urine bags",
    color: "#dc2626", soft: "#fee2e2", dark: "#7f1d1d",
    fact: "Red: contaminated recyclable plastic & rubber — autoclaved, shredded, sent to authorised recyclers.",
  },
  {
    id: "white", label: "White (Translucent)",
    sub: "Needles · Syringes · Sharps · Scalpels",
    color: "#475569", soft: "#f1f5f9", dark: "#0f172a",
    fact: "White (puncture-proof translucent): metallic sharps — disinfected, mutilated, deep-buried/recycled.",
  },
  {
    id: "blue", label: "Blue Bin",
    sub: "Glass vials · Ampules · Medicine bottles",
    color: "#2563eb", soft: "#dbeafe", dark: "#1e3a8a",
    fact: "Blue (cardboard with blue marking): broken/unbroken glass & metallic implants — disinfected and recycled.",
  },
];

const BIN_BY_ID = Object.fromEntries(BINS.map(b => [b.id, b]));

const ITEMS = [
  // Yellow
  { id: "diaper",       name: "Diaper",            bin: "yellow", Icon: Bandaids,   level: 1, fact: "Diapers are soiled waste — yellow." },
  { id: "pad",          name: "Sanitary Pad",      bin: "yellow", Icon: Drop,       level: 1, fact: "Sanitary pads are soiled waste — yellow." },
  { id: "soiled-gauze", name: "Soiled Gauze",      bin: "yellow", Icon: Bandaids,   level: 2, fact: "Blood-soaked dressings → yellow incineration." },
  { id: "anatomical",   name: "Anatomical Waste",  bin: "yellow", Icon: Heartbeat,  level: 3, fact: "Human anatomical waste must be incinerated — yellow." },
  // Red
  { id: "iv-set",       name: "IV Set",            bin: "red",    Icon: Drop,       level: 1, fact: "IV sets are contaminated plastics → red." },
  { id: "gloves",       name: "Disposable Gloves", bin: "red",    Icon: Hand,       level: 2, fact: "Used gloves are red — autoclaved + shredded." },
  { id: "urine-bag",    name: "Urine Bag",         bin: "red",    Icon: FirstAid,   level: 2, fact: "Urine bags & catheters → red disposables." },
  { id: "catheter",     name: "Catheter",          bin: "red",    Icon: FirstAid,   level: 3, fact: "Catheter tubing is red — recyclable plastic post-treatment." },
  // White (sharps)
  { id: "syringe",      name: "Syringe",           bin: "white",  Icon: Syringe,    level: 1, fact: "Syringes go in puncture-proof white containers." },
  { id: "needle",       name: "Hypodermic Needle", bin: "white",  Icon: Syringe,    level: 1, fact: "Sharps must be in white translucent containers." },
  { id: "scalpel",      name: "Scalpel",           bin: "white",  Icon: Syringe,    level: 3, fact: "Scalpels are sharps — white." },
  // Blue
  { id: "vial",         name: "Glass Vial",        bin: "blue",   Icon: TestTube,   level: 1, fact: "Glass vials → blue cardboard / container." },
  { id: "ampule",       name: "Ampule",            bin: "blue",   Icon: TestTube,   level: 2, fact: "Glass ampules go in blue bin." },
  { id: "med-bottle",   name: "Medicine Bottle",   bin: "blue",   Icon: Flask,      level: 1, fact: "Empty glass medicine bottles → blue." },
  { id: "tablet-strip", name: "Expired Tablets",   bin: "yellow", Icon: Pill,       level: 3, fact: "Expired drugs → yellow (incineration)." },
];

const LEVELS = [
  { id: 1, name: "Beginner",     count: 5,  desc: "Common household & ward waste" },
  { id: 2, name: "Intermediate", count: 7,  desc: "Clinical setting essentials" },
  { id: 3, name: "Advanced",     count: 10, desc: "Full institutional spectrum" },
];

/* ---------------- Audio engine (Web Audio API) ---------------- */
function makeAudio() {
  let ctx = null;
  const ensure = () => {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    return ctx;
  };
  const play = (steps) => {
    const c = ensure();
    const now = c.currentTime;
    steps.forEach(({ f, t = 0, d = 0.15, type = "sine", v = 0.18 }) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = type;
      o.frequency.setValueAtTime(f, now + t);
      g.gain.setValueAtTime(0.0001, now + t);
      g.gain.exponentialRampToValueAtTime(v, now + t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
      o.connect(g).connect(c.destination);
      o.start(now + t);
      o.stop(now + t + d + 0.02);
    });
  };
  return {
    correct: () => play([{ f: 880, d: 0.12 }, { f: 1320, t: 0.10, d: 0.18 }]),
    wrong:   () => play([{ f: 220, type: "sawtooth", d: 0.18, v: 0.18 },
                          { f: 165, t: 0.18, type: "sawtooth", d: 0.22, v: 0.18 }]),
    drop:    () => play([{ f: 520, d: 0.07, v: 0.12 }]),
    levelup: () => play([{ f: 523, d: 0.14 }, { f: 659, t: 0.12, d: 0.14 },
                          { f: 784, t: 0.24, d: 0.20 }, { f: 1046, t: 0.40, d: 0.30 }]),
  };
}

/* ---------------- Helpers ---------------- */
function pickItems(level) {
  // include all items up to & including current level, then sample
  const pool = ITEMS.filter(i => i.level <= level);
  const picked = LEVELS.find(l => l.id === level).count;
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(picked, shuffled.length));
}

/* ---------------- Main page ---------------- */
export default function SegregationGame() {
  const [phase, setPhase] = useState("intro"); // intro | play | done
  const [level, setLevel] = useState(1);
  const [queue, setQueue] = useState([]);
  const [results, setResults] = useState([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [feedback, setFeedback] = useState(null); // {ok, item, bin, fact}
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const binRefs = useRef({});

  useEffect(() => { document.title = "Segregation Challenge · Rescigo Health"; }, []);
  useEffect(() => { audioRef.current = makeAudio(); }, []);

  const sfx = (name) => {
    if (muted || !audioRef.current) return;
    try { audioRef.current[name]?.(); } catch (e) { /* noop */ }
  };

  const startLevel = (lv) => {
    setLevel(lv);
    setQueue(pickItems(lv));
    setResults([]);
    setStreak(0);
    setFeedback(null);
    setPhase("play");
  };

  const onDropItem = (item, binId) => {
    const correct = item.bin === binId;
    sfx(correct ? "correct" : "wrong");
    setResults(r => [...r, { item, dropped: binId, correct }]);
    setScore(s => s + (correct ? 100 + streak * 10 : -25));
    setStreak(s => {
      const next = correct ? s + 1 : 0;
      setBestStreak(bs => Math.max(bs, next));
      return next;
    });
    setFeedback({
      ok: correct, item,
      bin: BIN_BY_ID[correct ? item.bin : binId],
      correctBin: BIN_BY_ID[item.bin],
      fact: item.fact,
    });
    setQueue(q => q.slice(1));
  };

  // auto-advance feedback
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 1700);
    return () => clearTimeout(t);
  }, [feedback]);

  // detect level/game completion
  useEffect(() => {
    if (phase !== "play") return;
    if (queue.length === 0 && results.length > 0) {
      const allCorrect = results.every(r => r.correct);
      if (level < 3 && allCorrect) {
        sfx("levelup");
        setTimeout(() => startLevel(level + 1), 900);
      } else {
        setTimeout(() => setPhase("done"), 600);
      }
    }
  }, [queue, phase]);

  const current = queue[0];
  const next = queue[1];

  return (
    <div className="relative min-h-screen overflow-hidden text-white"
          data-testid="page-game"
          style={{
            background:
              "radial-gradient(circle at 12% 18%, rgba(34,211,168,0.18), transparent 45%)," +
              "radial-gradient(circle at 88% 82%, rgba(37,99,235,0.18), transparent 45%)," +
              "linear-gradient(to bottom, #06151a 0%, #04101a 100%)",
          }}>

      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)," +
                "linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}/>

      {/* Top bar */}
      <header className="relative z-30 flex items-center justify-between gap-4 px-6 pt-5">
        <Link to="/" data-testid="game-brand-home" className="flex items-center gap-2 group">
          <img src="/brand/rescigo-logo-white.png" alt="Rescigo Health" className="h-9 w-auto"/>
        </Link>
        <div className="flex items-center gap-3">
          <Stat label="Score" value={score} testid="game-score"/>
          <Stat label="Streak" value={`x${streak}`} testid="game-streak"/>
          {phase === "play" && (
            <Stat label="Level"
                  value={LEVELS.find(l => l.id === level).name}
                  testid="game-level"/>
          )}
          <button onClick={() => setMuted(m => !m)}
                  data-testid="game-mute"
                  className="w-10 h-10 rounded-full grid place-items-center bg-white/5 hover:bg-white/10 border border-white/10">
            {muted ? <SpeakerSlash size={16}/> : <SpeakerHigh size={16}/>}
          </button>
        </div>
      </header>

      <main className="relative z-20 px-4 sm:px-6 pb-12 pt-6 max-w-7xl mx-auto">
        {phase === "intro" && (
          <Intro onStart={() => startLevel(1)}/>
        )}

        {phase === "play" && (
          <Play
            current={current}
            next={next}
            onDropItem={onDropItem}
            binRefs={binRefs}
            queue={queue}
            level={level}
          />
        )}

        {phase === "done" && (
          <Done
            score={score}
            bestStreak={bestStreak}
            results={results}
            level={level}
            onRetry={() => { setScore(0); setBestStreak(0); startLevel(1); }}
          />
        )}
      </main>

      {/* Floating feedback */}
      <AnimatePresence>
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-sm w-[90%]"
            data-testid={`feedback-${feedback.ok ? "ok" : "no"}`}>
            <div className="rounded-xl border p-4 backdrop-blur"
                  style={{
                    background: feedback.ok ? "rgba(16,72,49,0.85)" : "rgba(80,16,16,0.85)",
                    borderColor: feedback.ok ? "#34d399" : "#f87171",
                    boxShadow: `0 10px 40px ${feedback.ok ? "rgba(52,211,153,0.35)" : "rgba(248,113,113,0.35)"}`,
                  }}>
              <div className="flex items-start gap-3">
                <div className="flex-none w-8 h-8 rounded-full grid place-items-center"
                      style={{ background: feedback.ok ? "#10b981" : "#ef4444" }}>
                  {feedback.ok
                    ? <ShieldCheck size={16} weight="bold"/>
                    : <Warning size={16} weight="bold"/>}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">
                    {feedback.ok ? "Compliance hit!" : `Belongs in ${feedback.correctBin.label}`}
                  </div>
                  <div className="text-xs text-white/80 mt-0.5">{feedback.fact}</div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------------- Sub-components ---------------- */
function Stat({ label, value, testid }) {
  return (
    <div className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 backdrop-blur"
          data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/50">{label}</div>
      <div className="text-sm font-semibold text-emerald-200 font-mono mt-0.5">{value}</div>
    </div>
  );
}

function Intro({ onStart }) {
  return (
    <div className="grid md:grid-cols-2 gap-10 items-center pt-8 md:pt-16"
          data-testid="game-intro">
      <div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200 text-[10px] uppercase tracking-[0.22em] font-semibold mb-7">
          <Sparkle size={11} weight="fill"/> Public Health Compliance Game
        </div>
        <h1 className="font-heading text-4xl md:text-6xl leading-[1.05]">
          Can you segregate biomedical waste correctly?
        </h1>
        <p className="text-base md:text-lg text-white/70 mt-6 leading-relaxed max-w-lg">
          Test your compliance skills against the Bio-Medical Waste Management
          Rules, 2016. Three levels. Drag, drop, and learn.
        </p>
        <div className="grid grid-cols-3 gap-3 mt-8 max-w-md">
          {LEVELS.map(l => (
            <div key={l.id} className="p-3 rounded-lg bg-white/5 border border-white/10">
              <div className="text-[9px] uppercase tracking-[0.22em] text-white/50">{l.name}</div>
              <div className="text-base font-semibold mt-1">{l.count} items</div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={onStart}
                  data-testid="game-start"
                  className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-emerald-400 text-[#04140d] font-semibold hover:bg-emerald-300 transition">
            <GameController size={15} weight="fill"/> Start the Challenge
            <ArrowRight size={13}/>
          </button>
          <Link to="/training" data-testid="game-learn"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 hover:border-white/40 text-sm font-medium">
            Learn the rules first
          </Link>
        </div>
      </div>

      {/* Bin preview */}
      <div className="grid grid-cols-2 gap-3">
        {BINS.map(b => (
          <BinPreview key={b.id} bin={b}/>
        ))}
      </div>
    </div>
  );
}

function BinPreview({ bin }) {
  return (
    <div className="rounded-xl p-5 border" style={{
      background: `linear-gradient(180deg, ${bin.color}26, transparent)`,
      borderColor: `${bin.color}55`,
    }}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-md" style={{ background: bin.color }}/>
        <div className="font-semibold">{bin.label}</div>
      </div>
      <div className="text-xs text-white/70 mt-2 leading-relaxed">{bin.sub}</div>
    </div>
  );
}

function Play({ current, next, onDropItem, binRefs, queue, level }) {
  return (
    <div className="pt-2 sm:pt-4" data-testid="game-play">
      {/* Progress */}
      <div className="text-center mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">
          {LEVELS.find(l => l.id === level).name} · {LEVELS.find(l => l.id === level).count - queue.length + 1}/{LEVELS.find(l => l.id === level).count}
        </div>
      </div>

      {/* Card stage */}
      <div className="relative h-[230px] sm:h-[260px] flex items-center justify-center mb-8">
        {next && (
          <div className="absolute opacity-30 scale-90 -translate-y-2 pointer-events-none">
            <WasteCard item={next} preview/>
          </div>
        )}
        {current && (
          <DraggableCard
            key={current.id + queue.length}
            item={current}
            binRefs={binRefs}
            onDrop={(binId) => onDropItem(current, binId)}
          />
        )}
        {!current && (
          <div className="text-white/60 text-sm italic">Calculating compliance score…</div>
        )}
      </div>

      {/* Bins */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {BINS.map(bin => (
          <Bin key={bin.id} bin={bin}
                ref={(el) => { binRefs.current[bin.id] = el; }}/>
        ))}
      </div>

      <div className="text-center text-[11px] text-white/40 mt-6 italic">
        Drag the card into the correct bin · or tap-and-hold on touch devices
      </div>
    </div>
  );
}

const Bin = React.forwardRef(function Bin({ bin }, ref) {
  return (
    <div ref={ref} data-bin-id={bin.id}
          data-testid={`bin-${bin.id}`}
          className="rounded-xl p-4 border-2 border-dashed transition relative overflow-hidden min-h-[140px]"
          style={{
            background: `linear-gradient(180deg, ${bin.color}1A, ${bin.color}05)`,
            borderColor: `${bin.color}88`,
          }}>
      <motion.div
        className="absolute -bottom-8 -right-8 w-24 h-24 rounded-full opacity-20"
        style={{ background: bin.color }}
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}/>
      <div className="relative">
        <div className="flex items-center gap-2">
          <Trash size={14} weight="fill" style={{ color: bin.color }}/>
          <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: bin.color }}>
            {bin.label}
          </div>
        </div>
        <div className="text-[10px] text-white/60 mt-2 leading-snug">{bin.sub}</div>
      </div>
    </div>
  );
});

function WasteCard({ item, preview = false }) {
  const Icon = item.Icon;
  return (
    <div className={`px-6 py-5 rounded-2xl border bg-white/5 backdrop-blur w-[260px] flex flex-col items-center gap-3
                     ${preview ? "" : "shadow-2xl"}`}
          style={{
            borderColor: "rgba(255,255,255,0.18)",
            boxShadow: preview ? "none" : "0 30px 80px rgba(16,185,129,0.18)",
          }}>
      <div className="w-16 h-16 rounded-xl grid place-items-center bg-gradient-to-br from-emerald-300/20 to-emerald-300/5 border border-emerald-300/20">
        <Icon size={30} weight="duotone" color="#a7f3d0"/>
      </div>
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.22em] text-white/50">Sort this</div>
        <div className="text-base font-semibold mt-0.5">{item.name}</div>
      </div>
    </div>
  );
}

function DraggableCard({ item, binRefs, onDrop }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const handleDragEnd = (_e, info) => {
    // hit-test against each bin element
    const px = info.point.x;
    const py = info.point.y;
    let hit = null;
    Object.entries(binRefs.current || {}).forEach(([id, el]) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (px >= r.left && px <= r.right && py >= r.top && py <= r.bottom) hit = id;
    });
    if (hit) {
      onDrop(hit);
    } else {
      // snap back
      x.set(0); y.set(0);
    }
  };

  return (
    <motion.div
      drag
      dragMomentum={false}
      dragElastic={0.18}
      style={{ x, y }}
      whileDrag={{ scale: 1.05, cursor: "grabbing" }}
      onDragEnd={handleDragEnd}
      data-testid={`waste-card-${item.id}`}
      className="cursor-grab active:cursor-grabbing select-none touch-none">
      <WasteCard item={item}/>
    </motion.div>
  );
}

function Done({ score, bestStreak, results, level, onRetry }) {
  const correct = results.filter(r => r.correct).length;
  const total = results.length;
  const accuracy = total ? Math.round((correct / total) * 100) : 0;
  const rank = accuracy >= 95 ? "Compliance Champion"
              : accuracy >= 80 ? "Waste Steward"
              : accuracy >= 60 ? "Apprentice"
              : "Needs Refresher";
  const emojiless = accuracy >= 95 ? "Excellent" : accuracy >= 80 ? "Strong" : "Keep practising";

  const share = async () => {
    const text = `I scored ${score} pts (${accuracy}% accuracy) on Rescigo's Biomedical Waste Segregation Challenge — ${rank}. Try it: ${window.location.origin}/game`;
    if (navigator.share) {
      try { await navigator.share({ title: "Rescigo Segregation Challenge", text }); return; }
      catch (e) { /* ignore */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      alert("Result copied to clipboard.");
    } catch (e) { alert(text); }
  };

  return (
    <div className="pt-12 max-w-2xl mx-auto text-center" data-testid="game-done">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-300/10 border border-emerald-300/30 text-emerald-200 text-[10px] uppercase tracking-[0.22em] font-semibold mb-7">
        <Trophy size={11} weight="fill"/> Challenge complete · {emojiless}
      </div>
      <div className="font-heading text-5xl md:text-6xl">
        {accuracy}%
      </div>
      <div className="text-sm text-white/60 mt-2">accuracy across all {total} items</div>

      <div className="grid grid-cols-3 gap-3 mt-9">
        <Tile label="Score" value={score}/>
        <Tile label="Best Streak" value={`x${bestStreak}`}/>
        <Tile label="Highest Level" value={LEVELS.find(l => l.id === level).name}/>
      </div>

      <div className="mt-9 rounded-xl border border-emerald-300/30 bg-emerald-300/5 p-5 text-left">
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-200">Your rank</div>
        <div className="font-heading text-xl mt-1">{rank}</div>
        <div className="text-xs text-white/70 mt-2">
          {accuracy >= 95
            ? "You'd pass an institutional BMW audit. Show this off — share with your team."
            : accuracy >= 60
              ? "Solid grasp of the basics. Brush up on the level-3 items to ace the next round."
              : "Worth taking the formal training before handling biomedical waste in the field."}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button onClick={onRetry}
                data-testid="game-retry"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-400 text-[#04140d] hover:bg-emerald-300 transition text-sm font-semibold">
          <ArrowsClockwise size={14} weight="bold"/> Play again
        </button>
        <button onClick={share}
                data-testid="game-share"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 hover:border-white/40 text-sm font-medium">
          <ShareNetwork size={14} weight="bold"/> Share my result
        </button>
        <Link to="/training" data-testid="game-cta-train"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-gradient-to-r from-amber-300/90 to-emerald-300/90 text-[#04140d] hover:opacity-95 transition text-sm font-semibold">
          Take the formal course <ArrowRight size={13}/>
        </Link>
        <Link to="/" data-testid="game-cta-home"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/15 hover:border-white/30 text-sm">
          <House size={13}/> Home
        </Link>
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="p-4 rounded-lg bg-white/5 border border-white/10">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{label}</div>
      <div className="font-heading text-2xl mt-1 text-emerald-200">{value}</div>
    </div>
  );
}
